import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Button,
  Stack,
  Dialog,
  Checkbox,
  TextField,
  InputAdornment,
  Divider,
  Avatar,
  LinearProgress,
  Card,
  CardContent,
  Tooltip,
  useMediaQuery,
  SvgIcon,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  Close as CloseIcon,
  LocalGasStation as FuelIcon,
  Fuel as FuelDropIcon,
  Speed as SpeedIcon,
  Navigation as NavigationIcon,
  Schedule as ClockIcon,
  BatteryFull as BatteryIcon,
  SignalCellularAlt as SignalIcon,
  Satellite as SatelliteIcon,
  PowerOff as PowerOffIcon,
  MyLocation as MyLocationIcon,
  HubOutlined as GeofenceIcon,
  KeyboardDoubleArrowRight as CollapseIcon,
  AccountTree as HistoryMapIcon,
  Videocam as VideoIcon,
  Send as ShareIcon,
  Add as PlusIcon,
  Remove as MinusIcon,
  InfoOutlined as InfoIcon,
  LayersOutlined as MapTypeIcon,
  Traffic as TrafficIcon,
  CenterFocusStrong as FitBoundsIcon,
  Search as SearchIcon,
  VpnKeyOutlined as KeyIcon,
  PlaceOutlined as PlacesIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { getNormalizedTelemetry, pickTelemetryValue } from '../../utils/telemetry';

import { useSocket } from '../../socket/SocketContext';
import { useGetUsersByRoleQuery } from '../../redux/admin/userApi';
import { useUpdateDeviceMutation } from '../../redux/admin/deviceApi';
import { useGetAllLiteDevicesQuery } from '../../redux/admin/adminApi';
import { useGetRoutesQuery } from '../../redux/logistics/logisticsApi';
import { getStatusConfig, getResolvedLiveSnapshot } from '../../utils/vehicleStatus';
import { resolveUiDeviceStatus } from '../../utils/uiDeviceStatus';
import { normalizeAcc } from '../../utils/normalizeIgnition';
import { fetchDeviceLiveData } from '../../utils/apiHelpers';
import { resolveTrackingState } from '../../utils/trackingStateEngine';
import { useStatus } from '../../popup/NotificationContext';
import api from '../../redux/apis/api';
import engineIcon from '../../assets/engine.svg';
import rightsideAuto from '../../assets/rightsideview/auto.png';
import rightsideBoat from '../../assets/rightsideview/boat.png';
import rightsidePickup from '../../assets/rightsideview/pickup.png';
import rightsideRacingCar from '../../assets/rightsideview/racingcar.png';
import rightsideAmbulance from '../../assets/rightsideview/ambulance.png';
import rightsideBike from '../../assets/rightsideview/bike.png';
import rightsideTaxiCar from '../../assets/rightsideview/taxicar.png';
import rightsidePoliceCar from '../../assets/rightsideview/policecar.png';
import rightsideCar from '../../assets/rightsideview/car.png';
import rightsideTractor from '../../assets/rightsideview/tractor.png';
import rightsideCrane from '../../assets/rightsideview/crane.png';
import rightsideTanker from '../../assets/rightsideview/tanker.png';
import rightsideBus from '../../assets/rightsideview/bus.png';
import rightsideTruck from '../../assets/rightsideview/truck.png';
import { beginProfile } from '../../utils/renderProfiler';
import { useDevRenderInstrumentation } from '../../utils/devInstrumentation';

const finishRightInfoPanelProfile = beginProfile("RightInfoPanel", { stage: "component" });

const ImmobilizationIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M7 4v2H4v2H2v6h2v2h2v2h2v-2h2v2h4v-2h2v2h2v-2h2V8h-2V6h-3V4H7zm-1 4h12v6H6V8zm2 1v3h3V9H8zm5 0v3h3V9h-3z" />
  </SvgIcon>
);

const RIGHT_PANEL_VEHICLE_ICONS = {
  auto: rightsideAuto,
  boat: rightsideBoat,
  pickup: rightsidePickup,
  racingcar: rightsideRacingCar,
  racecar: rightsideRacingCar,
  sportscar: rightsideRacingCar,
  ambulance: rightsideAmbulance,
  bike: rightsideBike,
  motorcycle: rightsideBike,
  scooty: rightsideBike,
  scooter: rightsideBike,
  taxicar: rightsideTaxiCar,
  taxi: rightsideTaxiCar,
  policecar: rightsidePoliceCar,
  police: rightsidePoliceCar,
  car: rightsideCar,
  jeep: rightsideCar,
  suv: rightsideCar,
  hatchback: rightsideCar,
  sedan: rightsideCar,
  tractor: rightsideTractor,
  crane: rightsideCrane,
  tanker: rightsideTanker,
  bus: rightsideBus,
  schoolbus: rightsideBus,
  truck: rightsideTruck,
  lorry: rightsideTruck,
};

const toTimestampMs = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d+$/.test(trimmed)) {
      const asNum = Number(trimmed);
      if (Number.isFinite(asNum)) return asNum < 1e12 ? asNum * 1000 : asNum;
    }
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

// A status response can arrive after a newer socket packet. Always compare the
// packet time, not response arrival time, before allowing it to replace panel
// telemetry.
const getTelemetryTimestampMs = (value = {}) => {
  const live = value?.liveData || value?.deviceLive || value?.liveSnapshot || {};
  return (
    toTimestampMs(
      value?.packetReceivedAt ??
        value?.lastSocketUpdate ??
        value?.packetTime ??
        value?.serverReceivedAt ??
        value?.receivedAt ??
        value?.correctedTime ??
        value?.timestamp ??
        value?.serverTime ??
        live?.serverReceivedAt ??
        live?.receivedAt ??
        live?.correctedTime ??
        live?.timestamp ??
        live?.serverTime ??
        value?.lastDataReceivedAt ??
        value?.lastUpdate
    )
  ) || 0;
};

const formatDisplayTime = (value) => {
  const ts = toTimestampMs(value);
  if (!ts) return "NA";
  return new Date(ts).toLocaleString();
};

const formatLocalDateInput = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDriftText = (driftMs = 0) => {
  const sign = driftMs >= 0 ? "+" : "-";
  const absMs = Math.abs(Number(driftMs) || 0);
  const totalMinutes = Math.round(absMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return `${sign}${parts.join(" ")}`;
};


const formatStatusLabel = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "inactive") return "NOT REPORTING";
  if (key === "no_data" || key === "nodata") return "NO DATA";
  return key.toUpperCase();
};

const formatNotReportingSince = (timestamp) => {
  const ts = toTimestampMs(timestamp);
  if (!ts) return "";
  const diff = Math.max(0, Date.now() - ts);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `since ${h}h ${m}m`;
};

const EMPTY_DAILY_STATS = {
  distance: 0,
  runningTime: 0,
  idleTime: 0,
  stopTime: 0,
  inactiveTime: 0,
  maxSpeed: 0,
  avgSpeed: 0,
  fuelConsumption: null,
};
const MOVING_STATUS_SPEED_KMH = Number(process.env.REACT_APP_MOVING_SPEED_KMH || 5);
const STATIONARY_FLICKER_GUARD_MS = 2 * 60 * 1000;



const formatAddress = (address) => {
  if (!address) return null;
  if (typeof address === 'string') return address;
  if (address.full) return address.full;
  
  const parts = [];
  if (address.street) parts.push(address.street);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.country) parts.push(address.country);
  
  return parts.length > 0 ? parts.join(', ') : null;
};

const normalizeAddressText = (value) => {
  const text = formatAddress(value);
  if (!text) return null;
  const normalized = text.trim();
  if (
    /^location at\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/i.test(normalized) ||
    /^(unknown location|unknown address|location unavailable|address unavailable|resolving address\.{0,3}|loading address\.{0,3}|n\/a|na|null)$/i.test(normalized)
  ) {
    return null;
  }
  return normalized;
};

const resolveTotalOdometerKm = (...sources) => {
  for (const source of sources) {
    const candidates = [
      source?.odometer,
      source?.totalDistance,
      source?.totalDistanceRawKm,
      source?.totalOdo,
      source?.totalOdoRawKm,
      source?.mileage,
    ];

    for (const candidate of candidates) {
      const numeric = Number(candidate);
      if (Number.isFinite(numeric) && numeric >= 0) {
        return numeric;
      }
    }
  }

  return 0;
};

const RightSidebarPanel = ({
  device, 
  socketUpdatesRef,
  onDeviceUpdated,
  isOpen,
  onClose, 
  getDirections, 
  handleCommand,
  onStartTrip, 
  onEndTrip, 
  onGetMyTrip,
  activeTrip, 
  onOpenCamera,
  onShowHistory, 
  onCreateGeofence,
  onGetMyRoute,
  onOpenReport,
  onSaveBookmark,
  showTraffic = false,
  setShowTraffic,
  mapType = 'roadmap',
  setMapType,
  followMode = false,
  setFollowMode,
  routesOverlayActive = false,
  hasSelectedDevice = false,
  mobileMode = false,
  bottomOffset = 0,
  onLocateMe,
  onFitBounds,
  onTogglePlacesView,
  placesViewActive = false,
  fullscreen = false,
  onToggleFullscreen,
  onZoomIn,
  onZoomOut,
  getVehicleIcon,
  onResolvedAddressChange,
}) => {
  const currentUser = useSelector((state) => state.auth?.user);
  const STALE_STATUS_DURATION_MS = 2 * 60 * 60 * 1000;
  const theme = useTheme();
  useEffect(() => {
    finishRightInfoPanelProfile({
      props: {
        deviceId: device?._id || null,
        isOpen,
      },
      state: {},
      deviceCount: device ? 1 : 0,
    });
  });

  const isViewportMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isLaptopViewport = useMediaQuery(theme.breakpoints.down("lg"));
  const isShortViewport = useMediaQuery("(max-height: 900px)");
  const isCompactDesktop = !isViewportMobile && (isLaptopViewport || isShortViewport);
  const isMobile = mobileMode || isViewportMobile;
  const panelWidth = isMobile
    ? "clamp(248px, calc((100vw - 16px) * 0.6), 380px)"
    : isCompactDesktop
      ? "clamp(226px, 15vw, 252px)"
      : "clamp(234px, 14vw, 264px)";
  const panelTextScale = isMobile ? 0.9 : isCompactDesktop ? 0.88 : 0.92;
  // ✅ ALL HOOKS AT TOP
  const [dailyStats, setDailyStats] = useState(EMPTY_DAILY_STATS);

  const panelRef = useRef(null);
  const { connected, getDeviceSocketData, on, joinDevice, leaveDevice } = useSocket();
  const { showStatus } = useStatus();
  const [updateDevice, { isLoading: isAssigningDriver }] = useUpdateDeviceMutation();
  const [assignDriverOpen, setAssignDriverOpen] = useState(false);
  const [driverSearch, setDriverSearch] = useState("");
  const [isUpdatingRouteState, setIsUpdatingRouteState] = useState(false);
  const [assignedDriver, setAssignedDriver] = useState({
    name: "",
    mobile: "",
    id: "",
  });
  const { data: routesPayload = [] } = useGetRoutesQuery();

  const [liveData, setLiveData] = useState({});
  const [panelSnapshot, setPanelSnapshot] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [isLive, setIsLive] = useState(false);
  
  const [resolvedAddress, setResolvedAddress] = useState(null);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const [refreshCounter, setRefreshCounter] = useState(0);
  const lastUpdateRef = useRef(0);
  const updateIntervalRef = useRef(null);
  const geocodeMetaRef = useRef({ key: '', lat: null, lng: null, at: 0 });
  const trackingStateRef = useRef(null);
  const assignedDriverDeviceIdRef = useRef("");
  const newestTelemetryTimestampRef = useRef(0);

  useDevRenderInstrumentation("RightInfoPanel", () => ({
    deviceId: device?._id || device?.id || "",
    isOpen,
    followMode,
    mapType,
    mobileMode: isMobile,
    routesOverlayActive,
  }));

  const { data: driversData = [] } = useGetUsersByRoleQuery("viewer", {
    skip: !assignDriverOpen,
  });
  const { data: allDevicesForDriverAssign = [] } = useGetAllLiteDevicesQuery(undefined, {
    skip: !assignDriverOpen,
  });

  const isExplicitStationaryStatus = useCallback((status) => {
    const key = String(status || "").trim().toLowerCase();
return ["idle", "stopped", "inactive", "expired", "offline", "no_data", "nodata"].includes(key);
  }, []);



  // Socket Listener
  useEffect(() => {
    if (!connected || !device?._id) return;

    const handleLocationUpdate = (data) => {
      const updateDeviceId = data.deviceId || data.device_id || data.id || data._id;
      const updateImei = String(data.imei || "");
      const matchesDevice =
        String(updateDeviceId || "") === String(device._id || "") ||
        (updateImei && String(device?.imei || "") === updateImei);
      if (!matchesDevice) return;

      const packetTimestamp =
        data.packetReceivedAt ??
        data.lastSocketUpdate ??
        data.packetTime ??
        data.serverReceivedAt ??
        data.receivedAt ??
        data.timestamp ??
        data.serverTime ??
        Date.now();
      const packetTimestampMs = getTelemetryTimestampMs({
        ...data,
        packetReceivedAt: packetTimestamp,
      });
      newestTelemetryTimestampRef.current = Math.max(
        newestTelemetryTimestampRef.current,
        packetTimestampMs
      );

      const lat = data.latitude ?? data.lat;
      const lng = data.longitude ?? data.lng;
      const hasCoords = typeof lat === 'number' && typeof lng === 'number' && lat !== 0 && lng !== 0;

      setLiveData(prev => {
        const nextRawSpeed =
          data.speed ??
          data.currentSpeed ??
          data.speedKmh ??
          data.speed_kmh ??
          data.velocity;
        const nextAccStatus =
          data.accStatus ?? data.acc ?? data.ignition ?? prev.accStatus ?? prev.acc ?? prev.ignition ?? null;
        const nextIgnition =
          data.ignition ?? data.accStatus ?? data.acc ?? prev.ignition ?? prev.accStatus ?? prev.acc ?? null;
        const incomingVehicleStatus =
          data.vehicleStatus ??
          data.vehicle_status ??
          null;
        const normalizedPanelStatus = String(
          panelSnapshot?.vehicleStatus ??
          panelSnapshot?.status ??
          prev?.vehicleStatus ??
          device?.vehicleStatus ??
          device?.status ??
          ""
        ).trim().toLowerCase();
        const normalizedIncomingStatus = String(incomingVehicleStatus || "").trim().toLowerCase();
        const safeIncomingVehicleStatus =
          normalizedIncomingStatus === "expired" && normalizedPanelStatus && normalizedPanelStatus !== "expired"
            ? prev.vehicleStatus ?? panelSnapshot?.vehicleStatus ?? panelSnapshot?.status ?? null
            : incomingVehicleStatus;
        const shouldForceZeroSpeed =
          (
            isExplicitStationaryStatus(String(safeIncomingVehicleStatus || "").trim().toLowerCase()) ||
            nextAccStatus === false
          ) &&
          (
            nextRawSpeed === null ||
            nextRawSpeed === undefined ||
            Number(nextRawSpeed) <= 0
          );
        const shouldKeepPrevSpeed =
          !shouldForceZeroSpeed &&
          !hasCoords &&
          Number(nextRawSpeed ?? prev?.speed ?? 0) === 0;
        const prevTimestampMs = toTimestampMs(prev?.timestamp ?? prev?.serverTime ?? prev?.deviceTime) || 0;
        const prevWasMovingRecently =
          prevTimestampMs > 0 &&
          Date.now() - prevTimestampMs <= STATIONARY_FLICKER_GUARD_MS &&
          Number(prev?.speed || 0) >= MOVING_STATUS_SPEED_KMH &&
          String(prev?.vehicleStatus || "").trim().toLowerCase() === "running";
        const shouldIgnoreTransientStationary =
          prevWasMovingRecently &&
          !hasCoords &&
          (
            String(safeIncomingVehicleStatus || "").trim().toLowerCase() === 'stopped' ||
            nextAccStatus === false
          ) &&
          (
            nextRawSpeed === null ||
            nextRawSpeed === undefined ||
            Number(nextRawSpeed) <= 0
          );
        const shouldKeepPrevStatus =
          !shouldIgnoreTransientStationary &&
          !hasCoords &&
          (
            safeIncomingVehicleStatus === null ||
            safeIncomingVehicleStatus === undefined ||
            safeIncomingVehicleStatus === '' ||
            (
              String(safeIncomingVehicleStatus).trim().toLowerCase() === 'stopped' &&
              Number(nextRawSpeed ?? prev?.speed ?? 0) <= 0
            )
          );
        const nextVehicleStatus = shouldIgnoreTransientStationary
          ? prev.vehicleStatus
          : resolveUiDeviceStatus(
              { ...device, ...(panelSnapshot || {}) },
              [{
                ...prev,
                ...data,
                accStatus: nextAccStatus,
                ignition: nextIgnition,
                speed: shouldForceZeroSpeed
                  ? 0
                  : shouldKeepPrevSpeed
                    ? prev.speed
                    : nextRawSpeed ?? prev.speed,
                vehicleStatus: shouldKeepPrevStatus ? prev.vehicleStatus : safeIncomingVehicleStatus ?? prev.vehicleStatus,
                timestamp: packetTimestamp,
                packetTime: packetTimestamp,
                lastSocketUpdate: Date.now(),
              }]
            ).status;

        console.log("[SPEED_FLOW][UI]", String(device?.imei || device?._id || ""), {
          stage: "RightInfoPanel.socket",
          incomingSpeed: nextRawSpeed ?? null,
          appliedSpeed: shouldForceZeroSpeed ? 0 : shouldKeepPrevSpeed ? prev.speed : nextRawSpeed ?? prev.speed,
          incomingVehicleStatus: safeIncomingVehicleStatus,
          appliedVehicleStatus: nextVehicleStatus,
          hasCoords,
        });

        const socketTelemetry = getNormalizedTelemetry([data, prev]);

        return {
          ...prev,
          latitude: hasCoords ? lat : prev.latitude,
          longitude: hasCoords ? lng : prev.longitude,
          speed: shouldIgnoreTransientStationary
            ? prev.speed
            : shouldForceZeroSpeed
            ? 0
            : shouldKeepPrevSpeed
            ? prev.speed
            : nextRawSpeed ?? prev.speed,
          heading: data.heading ?? data.course ?? data.bearing ?? prev.heading,
          accStatus: shouldIgnoreTransientStationary ? prev.accStatus : nextAccStatus,
          acc: shouldIgnoreTransientStationary ? prev.acc : nextAccStatus,
          ignition: shouldIgnoreTransientStationary ? prev.ignition : nextIgnition,
          vehicleStatus: nextVehicleStatus,
          movementState: data.movementState ?? data.movement_state ?? prev.movementState,
          fuelLevel: data.fuelLevel ?? data.fuel ?? prev.fuelLevel,
          battery: socketTelemetry.battery ?? prev.battery,
          voltage: socketTelemetry.voltage ?? prev.voltage,
          satellites: data.satellites ?? data.sats ?? prev.satellites,
          mcc: data.mcc ?? prev.mcc,
          mnc: data.mnc ?? prev.mnc,
          lac: data.lac ?? prev.lac,
          cellId: data.cellId ?? prev.cellId,
          radioType: data.radioType ?? prev.radioType,
          rssi: data.rssi ?? data.signalStrength ?? prev.rssi,
          temperature: data.temperature ?? data.temp ?? prev.temperature,
          address: formatAddress(data.address) ?? prev.address,
          timestamp: packetTimestamp,
          deviceTime: data.deviceTime ?? prev.deviceTime ?? null,
          serverTime: data.serverTime ?? prev.serverTime ?? packetTimestamp,
          correctedTime: data.correctedTime ?? prev.correctedTime ?? packetTimestamp,
          isDrifted: Boolean(data.isDrifted ?? prev.isDrifted),
          driftMs: Number(data.driftMs ?? prev.driftMs ?? 0) || 0,
          driftSeconds: Number(data.driftSeconds ?? prev.driftSeconds ?? 0) || 0,
          packetTime: packetTimestamp,
          packetReceivedAt: Date.now(),
          lastSocketUpdate: Date.now(),
          locationInherited: data.locationInherited ?? (!hasCoords),
          hasLiveLocation: hasCoords,
        };
      });

      setLastUpdateTime(Date.now());
      setIsLive(true);
      setRefreshCounter(prev => prev + 1);
      lastUpdateRef.current = Date.now();
    };

    joinDevice?.(device._id);
    const offPacket = on?.('gps_packet', handleLocationUpdate);
    const offGps = on?.('gps_update', handleLocationUpdate);
    const offLocation = on?.('location_update', handleLocationUpdate);
    const offStatus = on?.('status_update', handleLocationUpdate);
    const offVehicle = on?.('vehicle_update', handleLocationUpdate);
    const offDevice = on?.('device_update', handleLocationUpdate);

    return () => {
      offPacket?.();
      offGps?.();
      offLocation?.();
      offStatus?.();
      offVehicle?.();
      offDevice?.();
      leaveDevice?.(device._id);
    };
  }, [connected, on, joinDevice, leaveDevice, device?._id, device?.imei, panelSnapshot]);

  // Auto-refresh
  useEffect(() => {
    if (!isOpen) return;
    updateIntervalRef.current = setInterval(() => {
      // MapContainer mutates its live socket ref without changing its object
      // identity. Re-read it while this panel is open even when this panel
      // missed the matching socket event; otherwise an old REST `stopped`
      // snapshot can remain visible after the map has already resolved Idle.
      setRefreshCounter(prev => prev + 1);
    }, 2000);
    return () => clearInterval(updateIntervalRef.current);
  }, [isOpen]);

  // Reset on device change
  useEffect(() => {
    if (!device) {
      setLiveData({});
      setPanelSnapshot(null);
      setLastUpdateTime(null);
      setIsLive(false);
      setResolvedAddress(null);
      onResolvedAddressChange?.("");
      setCollapsed(true);
      setRefreshCounter(0);
      lastUpdateRef.current = 0;
      trackingStateRef.current = null;
      newestTelemetryTimestampRef.current = 0;
      return;
    }
    setLiveData({});
    setPanelSnapshot(null);
    setLastUpdateTime(null);
    setIsLive(false);
    setResolvedAddress(null);
    onResolvedAddressChange?.("");
    setCollapsed(false);
    setRefreshCounter(0);
    lastUpdateRef.current = 0;
    trackingStateRef.current = null;
    newestTelemetryTimestampRef.current = getTelemetryTimestampMs(device);
  }, [device?._id, onResolvedAddressChange]);

  useEffect(() => {
    if (!device?._id) {
      assignedDriverDeviceIdRef.current = "";
      setAssignedDriver({ name: "", mobile: "", id: "" });
      return;
    }

    const nextAssignedDriver = {
      id: String(device?.driverId || device?.driver?.driverId || ""),
      name:
        device?.driver?.driverName ||
        device?.driverName ||
        device?.driver?.name ||
        "",
      mobile:
        device?.driver?.contactNumber ||
        device?.driverContact ||
        device?.driverNumber ||
        device?.mobileNumber ||
        device?.driver?.mobileNo ||
        "",
    };

    const hasIncomingDetails = Boolean(nextAssignedDriver.name || nextAssignedDriver.mobile);
    const deviceChanged =
      assignedDriverDeviceIdRef.current !== String(device?._id || "");

    setAssignedDriver((prev) => {
      const hasLocalDriver = Boolean(prev.name || prev.mobile || prev.id);
      if (deviceChanged) return nextAssignedDriver;

      // If backend/device payload has usable driver details, treat it as source of truth.
      if (hasIncomingDetails) return nextAssignedDriver;

      // If only driver id is present but name/mobile are missing, don't clobber local UI state.
      if (nextAssignedDriver.id) {
        const sameId = String(prev.id || "") === String(nextAssignedDriver.id || "");
        if (sameId) return { ...prev, id: nextAssignedDriver.id };
        return nextAssignedDriver;
      }

      // No incoming driver; clear only if we didn't already have something.
      if (!hasLocalDriver) return nextAssignedDriver;
      return prev;
    });
    assignedDriverDeviceIdRef.current = String(device?._id || "");
  }, [device]);

  // Poll live data
  useEffect(() => {
    if (!isOpen || !device?._id) return;
    let cancelled = false;

    const pullLive = async () => {
      try {
        const latest = await fetchDeviceLiveData(device._id);
        if (cancelled || !latest) return;
        const incomingTimestampMs = getTelemetryTimestampMs(latest);
        const freshestSocketTimestampMs = newestTelemetryTimestampRef.current;
        // A delayed REST snapshot must never roll a newer socket packet back
        // from Running/Idle to its old persisted Stopped value. An unversioned
        // response is also unsafe once we have a versioned live packet.
        if (
          freshestSocketTimestampMs > 0 &&
          (!incomingTimestampMs || incomingTimestampMs < freshestSocketTimestampMs)
        ) {
          return;
        }
        newestTelemetryTimestampRef.current = Math.max(
          freshestSocketTimestampMs,
          incomingTimestampMs
        );
        setPanelSnapshot((prev) => ({
          ...(prev || {}),
          ...latest,
        }));
        setLiveData((prev) => ({
          ...prev,
          ...(latest.liveData || {}),
        }));
      } catch (err) {
        // keep socket + list fallback
      }
    };

    pullLive();
    const id = setInterval(pullLive, 15000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isOpen, device?._id]);

  // Computed data
  const computedData = useMemo(() => {
    if (!device) {
      return {
        speed: 0, lat: null, lng: null, heading: 0, accStatus: false,
        vehicleStatus: 'no_data', source: 'none', dataAge: Infinity,
        isFresh: false, freshness: null
      };
    }

    const getPanelTimestamp = (value) =>
      toTimestampMs(
        value?.packetReceivedAt ??
          value?.lastSocketUpdate ??
          value?.packetTime ??
          value?.serverReceivedAt ??
          value?.receivedAt ??
          value?.timestamp ??
          value?.serverTime ??
          value?.lastDataReceivedAt ??
          value?.lastUpdate
      ) || 0;

    const socketById = getDeviceSocketData?.(device?._id) || {};
    const socketByImei = getDeviceSocketData?.(device?.imei) || {};
    const socketByIdTs =
      toTimestampMs(
        socketById.packetReceivedAt ??
          socketById.lastSocketUpdate ??
          socketById.packetTime ??
          socketById.serverReceivedAt ??
          socketById.receivedAt ??
          socketById.timestamp ??
          socketById.serverTime
      ) || 0;
    const socketByImeiTs =
      toTimestampMs(
        socketByImei.packetReceivedAt ??
          socketByImei.lastSocketUpdate ??
          socketByImei.packetTime ??
          socketByImei.serverReceivedAt ??
          socketByImei.receivedAt ??
          socketByImei.timestamp ??
          socketByImei.serverTime
      ) || 0;
    const contextSocketData = socketByImeiTs > socketByIdTs ? socketByImei : socketById;
    const mapSocketData =
      socketUpdatesRef?.current?.[device?._id] ||
      socketUpdatesRef?.current?.[device?.imei] ||
      {};
    // MapContainer is the live-tracking source of truth. Prefer its packet so
    // the panel cannot resolve a different status from a secondary socket
    // cache or its REST polling snapshot.
    const rawSocketData = Object.keys(mapSocketData).length
      ? mapSocketData
      : contextSocketData;
    const now = Date.now();
    const panelDevice = {
      ...(device || {}),
      ...(panelSnapshot || {}),
      status: panelSnapshot?.status ?? device?.status,
      vehicleStatus: panelSnapshot?.vehicleStatus ?? device?.vehicleStatus,
      movementStatus: panelSnapshot?.movementStatus ?? device?.movementStatus,
      deviceLicense: panelSnapshot?.deviceLicense ?? device?.deviceLicense,
      licenseRuntime: panelSnapshot?.licenseRuntime ?? device?.licenseRuntime,
      lastDataReceivedAt: panelSnapshot?.lastDataReceivedAt ?? device?.lastDataReceivedAt,
      currentLocation: {
        ...(device?.currentLocation || {}),
        ...(panelSnapshot?.currentLocation || {}),
      },
      liveData: {
        ...(device?.liveData || {}),
        ...(panelSnapshot?.liveData || {}),
      },
    };
    const panelStatusKey = String(
      panelSnapshot?.vehicleStatus ??
      panelSnapshot?.status ??
      panelDevice?.vehicleStatus ??
      panelDevice?.status ??
      ""
    ).trim().toLowerCase();
    const socketStatusKey = String(
      rawSocketData?.vehicleStatus ??
      rawSocketData?.status ??
      ""
    ).trim().toLowerCase();
    const socketData =
      socketStatusKey === "expired" && panelStatusKey && panelStatusKey !== "expired"
        ? {
            ...rawSocketData,
            vehicleStatus: panelSnapshot?.vehicleStatus ?? panelSnapshot?.status ?? panelDevice?.vehicleStatus ?? null,
            status: panelSnapshot?.status ?? panelSnapshot?.vehicleStatus ?? panelDevice?.status ?? null,
          }
        : rawSocketData;
    const deviceStatus = resolveUiDeviceStatus(panelDevice, [socketData, liveData, panelSnapshot]).status;
    const isExpiredDevice = deviceStatus === 'expired';
    const deviceLiveData =
      isExpiredDevice
        ? panelDevice?.liveData || {}
        : liveData && Object.keys(liveData).length > 0
          ? liveData
          : panelDevice?.liveData || {};

    const effectiveSocketData = isExpiredDevice ? {} : socketData;
    const socketTs = getPanelTimestamp(effectiveSocketData);
    const dbTs = Math.max(
      getPanelTimestamp(deviceLiveData),
      getPanelTimestamp(panelDevice?.deviceLive),
      getPanelTimestamp(panelDevice?.liveSnapshot),
      getPanelTimestamp(panelDevice?.liveData),
      toTimestampMs(panelDevice?.lastDataReceivedAt) || 0
    );

    const freshestTs = Math.max(socketTs, dbTs);
    const age = freshestTs ? now - freshestTs : Infinity;
    const hasFreshSignal = freshestTs > 0 && age <= STALE_STATUS_DURATION_MS;

    const mergedLiveSource = isExpiredDevice
      ? {}
      : {
          ...(panelDevice?.liveData || {}),
          ...(panelDevice?.deviceLive || {}),
          ...(panelDevice?.liveSnapshot || {}),
          ...(deviceLiveData || {}),
          ...(effectiveSocketData || {}),
        };
    const resolvedDevice = {
      ...(panelDevice || {}),
      liveData: {
        ...(panelDevice?.liveData || {}),
        ...(mergedLiveSource || {}),
      },
      currentLocation: {
        ...(panelDevice?.currentLocation || {}),
        ...(mergedLiveSource || {}),
      },
    };
    const finalData = socketTs >= dbTs ? effectiveSocketData : deviceLiveData;
    const snapshot = getResolvedLiveSnapshot(resolvedDevice, mergedLiveSource);
    const status = isExpiredDevice
      ? 'expired'
      : resolveUiDeviceStatus(resolvedDevice, [effectiveSocketData, liveData, deviceLiveData, panelSnapshot]).status;
    const lat = snapshot.latitude ?? finalData.latitude ?? finalData.lat ?? panelDevice.currentLocation?.latitude;
    const lng = snapshot.longitude ?? finalData.longitude ?? finalData.lng ?? panelDevice.currentLocation?.longitude;
    const rawSpeed = Number(snapshot.speed ?? finalData.speed ?? 0);
    const accSignal = normalizeAcc(snapshot.accStatus ?? finalData.accStatus);
    const displaySpeed = isExpiredDevice ? 0 : rawSpeed;
    const rawIdleSinceMs = Number(
      finalData.idleSinceMs ??
      finalData.idleSince ??
      deviceLiveData.idleSinceMs ??
      deviceLiveData.idleSince ??
      device?.liveData?.idleSinceMs ??
      device?.liveData?.idleSince ??
      0
    );
    const prevTracking = trackingStateRef.current;
    const tracking = resolveTrackingState({
      prev: prevTracking,
      speed: displaySpeed,
      accStatus: isExpiredDevice
        ? false
        : snapshot.accStatus ?? finalData.accStatus ?? finalData.acc ?? finalData.ignition ?? null,
      timestampMs: freshestTs || now,
      latitude: Number.isFinite(Number(lat)) ? Number(lat) : null,
      longitude: Number.isFinite(Number(lng)) ? Number(lng) : null,
      heading: finalData.heading ?? finalData.course ?? finalData.bearing ?? 0,
      vehicleStatus: finalData.vehicleStatus ?? finalData.vehicle_status ?? finalData.status ?? status,
      movementState: finalData.movementState ?? finalData.movement_state ?? null,
    });
    const resolvedStatus = status;
    const telemetrySources = [
      effectiveSocketData,
      liveData,
      deviceLiveData,
      panelSnapshot?.liveData,
      panelSnapshot?.currentLocation,
      panelDevice?.liveData,
      panelDevice?.currentLocation,
      mergedLiveSource,
      finalData,
    ];
    const totalOdometerKm = resolveTotalOdometerKm(
      snapshot,
      finalData,
      effectiveSocketData,
      deviceLiveData,
      device?.liveData,
      device?.currentLocation,
      panelSnapshot?.liveData,
      panelSnapshot?.currentLocation,
      panelSnapshot,
      panelDevice?.liveData,
      panelDevice?.currentLocation,
      panelDevice,
      device
    );
    const prevStatus = String(prevTracking?.vehicleStatus || "").toLowerCase();
    const prevStatusSinceMs = Number(prevTracking?.statusSinceMs || 0);
    let statusSinceMs = hasFreshSignal ? (freshestTs || now) : null;

    if (!hasFreshSignal) {
      statusSinceMs = null;
    } else if (String(resolvedStatus || "").toLowerCase() === prevStatus && prevStatusSinceMs > 0) {
      statusSinceMs = prevStatusSinceMs;
    } else if (resolvedStatus === 'stopped') {
      statusSinceMs = Number(tracking?.lastZeroSpeedAtMs || 0) || freshestTs || now;
    } else if (resolvedStatus === 'idle') {
      statusSinceMs = rawIdleSinceMs > 0 ? rawIdleSinceMs : (freshestTs || now);
    } else if (resolvedStatus === 'running') {
      statusSinceMs = Number(tracking?.lastMovingAtMs || 0) || freshestTs || now;
    }

    trackingStateRef.current = {
      ...tracking,
      vehicleStatus: resolvedStatus,
      statusSinceMs,
    };

    const normalizedTelemetry = getNormalizedTelemetry(telemetrySources);

    return {
      speed: displaySpeed,
      lat,
      lng,
      heading: finalData.heading ?? finalData.course ?? finalData.bearing ?? 0,
      accStatus: accSignal,
      vehicleStatus: resolvedStatus,
      fuelLevel: pickTelemetryValue(telemetrySources, ["fuelLevel", "fuel"]),
      battery: normalizedTelemetry.battery,
      voltage: normalizedTelemetry.voltage,
      satellites: finalData.satellites ?? null,
      rssi: finalData.rssi ?? null,
      temperature: pickTelemetryValue(telemetrySources, ["temperature", "temp"]),
      totalOdometerKm,
      address: formatAddress(finalData.address) ?? null,
      deviceTime: finalData.deviceTime ?? deviceLiveData.deviceTime ?? null,
      serverTime: finalData.serverTime ?? deviceLiveData.serverTime ?? null,
      correctedTime:
        finalData.correctedTime ??
        deviceLiveData.correctedTime ??
        freshestTs,
      isDrifted: Boolean(finalData.isDrifted ?? deviceLiveData.isDrifted),
      driftMs: Number(finalData.driftMs ?? deviceLiveData.driftMs ?? 0) || 0,
      driftSeconds: Number(finalData.driftSeconds ?? deviceLiveData.driftSeconds ?? 0) || 0,
      source: isExpiredDevice ? 'database' : socketTs >= dbTs ? 'realtime' : 'database',
      dataAge: age,
      hasFreshSignal,
      isFresh: null,
      freshness: null,
      timestamp: freshestTs,
      statusSinceMs,
    };
  }, [device, liveData, panelSnapshot, getDeviceSocketData, refreshCounter, socketUpdatesRef]);

  const networkParams = useMemo(() => {
    const candidates = [
      liveData,
      panelSnapshot?.liveData,
      panelSnapshot?.currentLocation,
      panelSnapshot,
      device?.liveData,
      device?.currentLocation,
      device,
    ].filter(Boolean);

    const pickValue = (key) => {
      for (const candidate of candidates) {
        const value = candidate?.[key];
        if (value !== undefined && value !== null && value !== '') {
          return value;
        }
      }
      return null;
    };

    return {
      radioType: pickValue('radioType') || 'gsm',
      cellId: pickValue('cellId'),
      lac: pickValue('lac'),
      mcc: pickValue('mcc'),
      mnc: pickValue('mnc'),
    };
  }, [device, liveData, panelSnapshot]);

  // Address resolution
  useEffect(() => {
    const incomingAddress = normalizeAddressText(computedData.address);
    if (incomingAddress) {
      setResolvedAddress(incomingAddress);
      onResolvedAddressChange?.(incomingAddress);
    }
  }, [computedData.address, onResolvedAddressChange]);

  // Fetch geocode address
  useEffect(() => {
    const lat = Number(computedData.lat);
    const lng = Number(computedData.lng);
    if (!device?._id || !Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
      return;
    }

    const currentPos = { lat, lng };
    const deviceKey = String(device._id);
    const now = Date.now();

    const distanceMeters = (a, b) => {
      if (!a || !b) return true;
      const R = 6371000;
      const dLat = (b.lat - a.lat) * Math.PI / 180;
      const dLng = (b.lng - a.lng) * Math.PI / 180;
      const x = dLat * dLat + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * dLng * dLng;
      return Math.sqrt(x) * R;
    };

    const prev = geocodeMetaRef.current;
    const moved = prev.key !== deviceKey ? Infinity : distanceMeters(
      { lat: prev.lat, lng: prev.lng },
      currentPos
    );
    const elapsed = prev.key !== deviceKey ? Infinity : now - prev.at;

    if (moved < 20 && elapsed < 45000) return;

    let cancelled = false;

    const fetchAddress = async () => {
      setIsLoadingAddress(true);

      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_BACKEND_URL || 'https://qylmox.in/api';

        const response = await fetch(
          `${baseUrl}/devices/${device._id}/address?lat=${currentPos.lat}&lng=${currentPos.lng}`,
          { headers: { 'x-auth-token': token, 'Content-Type': 'application/json' } }
        );

        if (!response.ok) throw new Error('Address fetch failed');
        const data = await response.json();

        if (!cancelled) {
          const normalized =
            normalizeAddressText(data.address) ||
            normalizeAddressText(data.address?.full) ||
            normalizeAddressText(data.address?.short);
          if (normalized) {
            setResolvedAddress(normalized);
            onResolvedAddressChange?.(normalized);
          }
          geocodeMetaRef.current = {
            key: deviceKey,
            lat: currentPos.lat,
            lng: currentPos.lng,
            at: Date.now(),
          };
        }
      } catch (err) {
        // handle error silently
      } finally {
        if (!cancelled) setIsLoadingAddress(false);
      }
    };

    const timeoutId = setTimeout(fetchAddress, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [device?._id, computedData.lat, computedData.lng, onResolvedAddressChange]);

  const hasNeverReportedData = useMemo(() => {
    const statusKey = String(computedData?.vehicleStatus || "").trim().toLowerCase();
    if (statusKey === "expired") return false;
    if (statusKey === "no_data" || statusKey === "nodata") return true;

    const latestKnownPacketMs = Math.max(
      toTimestampMs(computedData?.timestamp) || 0,
      toTimestampMs(computedData?.deviceTime) || 0,
      toTimestampMs(computedData?.serverTime) || 0,
      toTimestampMs(computedData?.correctedTime) || 0,
      toTimestampMs(device?.lastDataReceivedAt) || 0,
      toTimestampMs(device?.liveData?.timestamp) || 0,
      toTimestampMs(device?.deviceLive?.timestamp) || 0,
      toTimestampMs(device?.liveSnapshot?.timestamp) || 0,
    );

    return latestKnownPacketMs <= 0;
  }, [
    computedData?.correctedTime,
    computedData?.deviceTime,
    computedData?.serverTime,
    computedData?.timestamp,
    computedData?.vehicleStatus,
    device?.deviceLive?.timestamp,
    device?.lastDataReceivedAt,
    device?.liveData?.timestamp,
    device?.liveSnapshot?.timestamp,
  ]);

  // Fetch daily stats
  useEffect(() => {
    if (!device?._id) return;
    if (hasNeverReportedData) {
      setDailyStats(EMPTY_DAILY_STATS);
      return;
    }
    
    const fetchDailyStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const today = formatLocalDateInput(new Date());
        const apiBase = process.env.REACT_APP_API_URL || 'https://qylmox.in/api';
        const headers = { 'Authorization': `Bearer ${token}`, 'x-auth-token': token };
        const [response, fuelResponse] = await Promise.all([
          fetch(`${apiBase}/history/${device._id}/summary?date=${today}`, {
            headers,
            signal: AbortSignal.timeout(5000),
          }),
          device?.imei
            ? fetch(`${apiBase}/vehicles/${device.imei}/fuel-consumption?from=${today}&to=${today}`, {
                headers,
                signal: AbortSignal.timeout(5000),
              })
            : Promise.resolve(null),
        ]);
        
        if (response.ok) {
          const data = await response.json();
          const fuelData = fuelResponse?.ok ? await fuelResponse.json() : null;
          const mileageFuel = fuelData?.summary?.fuel_consumption_liters;
          if (data.hasData && data.summary) {
            setDailyStats({
              distance: Math.max(0, data.summary.totalDistance || 0),
              runningTime: Math.max(0, data.summary.movingTime || 0),
              idleTime: data.summary.idleTime || 0,
              stopTime: data.summary.stopTime || 0,
              inactiveTime: data.summary.inactiveTime || 0,
              maxSpeed: data.summary.maxSpeed || 0,
              avgSpeed: data.summary.avgSpeed || 0,
              fuelConsumption: Number.isFinite(Number(mileageFuel)) ? Number(mileageFuel) : null,
            });
          } else {
            setDailyStats(EMPTY_DAILY_STATS);
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch daily stats:', err);
        }
        setDailyStats(EMPTY_DAILY_STATS);
      }
    };
    
    fetchDailyStats();
    const interval = setInterval(fetchDailyStats, 60000);
    return () => clearInterval(interval);
  }, [device?._id, device?.imei, hasNeverReportedData]);

  // Helper functions
  const getStatusColor = useCallback(() => {
    const status = computedData.vehicleStatus;
    if (status === 'running') return '#16a34a';
    if (status === 'idle') return '#ca8a04';
    if (status === 'stopped') return '#dc2626';
    if (status === 'inactive' || status === 'expired' || status === 'no_data' || status === 'nodata') {
      return '#4338ff';
    }
    return '#6b7280';
  }, [computedData.vehicleStatus]);

  const getElapsedTime = useCallback(() => {
    if (computedData.hasFreshSignal === false) return 'No recent data';
    const updateTime =
      computedData.statusSinceMs ||
      computedData.timestamp ||
      lastUpdateTime ||
      (device?.lastDataReceivedAt ? new Date(device.lastDataReceivedAt).getTime() : null);

    if (!updateTime) return 'No signal';

    const secondsAgo = Math.floor((Date.now() - updateTime) / 1000);
    const safeSeconds = Math.max(0, secondsAgo);
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, [computedData.statusSinceMs, computedData.timestamp, lastUpdateTime, device?.lastDataReceivedAt]);

const statusColor = getStatusColor();
const isNotReporting = computedData.vehicleStatus === "inactive";
const isNoDataDevice = hasNeverReportedData;
const hasDevice = Boolean(device?._id || device?.id);
const isRailOnly = !hasDevice || collapsed;
const normalizedStatusKey = String(computedData.vehicleStatus || '').trim().toLowerCase();
const statusIconTheme = useMemo(() => {
  const isBlueFamily = ['inactive', 'expired', 'no_data', 'nodata'].includes(normalizedStatusKey);
  const isRunningFamily = ['running', 'idle'].includes(normalizedStatusKey);

  return {
    gps: '#7b8794',
    ignition: isRunningFamily ? '#138a52' : '#e63946',
    battery: isBlueFamily ? '#ef4444' : '#16a34a',
    power: '#16a34a',
    immobilization: '#e63946',
    badge: statusColor,
  };
}, [normalizedStatusKey, statusColor]);
const sectionCardSx = {
  p: 1.35,
  mx: 0,
  mb: 1.15,
  bgcolor: '#ffffff',
  borderRadius: 0,
  border: '1px solid #e5e7eb',
  boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
};
const sectionTitleSx = {
  fontSize: '0.84rem',
  fontWeight: 900,
  color: '#1f2937',
  mb: 1.15,
  display: 'block',
};
const infoRowLabelSx = {
  fontSize: '0.78rem',
  color: '#3f3f46',
  fontWeight: 500,
};
const infoRowValueSx = {
  fontSize: '0.78rem',
  color: '#1f2937',
  fontWeight: 500,
  textAlign: 'right',
};
const activityTimeLabel = (seconds) =>
  `${Math.floor((Number(seconds) || 0) / 3600).toString().padStart(2, '0')}:${Math.floor(((Number(seconds) || 0) % 3600) / 60)
    .toString()
    .padStart(2, '0')} hrs`;

  const availableDrivers = useMemo(() => {
    const rows = Array.isArray(driversData) ? driversData : driversData?.data || [];
    const q = String(driverSearch || "").trim().toLowerCase();
    const normalized = rows.map((driver) => ({
      ...driver,
      label: driver?.username || driver?.name || "Driver",
      mobile: driver?.mobileNo || driver?.mobile || "",
      id: String(driver?._id || driver?.id || ""),
    }));

    if (!q) return normalized;

    return normalized
      .filter((driver) =>
        driver.label.toLowerCase().includes(q) ||
        String(driver.mobile || "").includes(q)
      )
      .sort((a, b) => {
        const aStarts = a.label.toLowerCase().startsWith(q) ? 0 : 1;
        const bStarts = b.label.toLowerCase().startsWith(q) ? 0 : 1;
        return aStarts - bStarts || a.label.localeCompare(b.label);
      });
  }, [driverSearch, driversData]);

  const normalizedDriverAssignmentDevices = useMemo(() => {
    if (Array.isArray(allDevicesForDriverAssign)) return allDevicesForDriverAssign;
    if (Array.isArray(allDevicesForDriverAssign?.data)) return allDevicesForDriverAssign.data;
    return [];
  }, [allDevicesForDriverAssign]);

  const handleAssignDriver = useCallback(async (driver) => {
    if (!device?._id || !driver?.id) return;

    const conflictingDevice = normalizedDriverAssignmentDevices.find((row) => {
      const rowId = String(row?._id || row?.id || "");
      const rowDriverId = String(row?.driver?.driverId || row?.driverId || "");
      return rowId && rowId !== String(device?._id || "") && rowDriverId === String(driver.id);
    });

    if (conflictingDevice) {
      showStatus(
        "error",
        `This driver is already assigned to ${conflictingDevice?.name || conflictingDevice?.imei || "another device"}.`
      );
      return;
    }

    try {
      const response = await updateDevice({
        id: device._id,
        driverId: driver.id,
        driverName: driver.label,
        driverContact: driver.mobile || "",
        driver: {
          driverId: driver.id,
          driverName: driver.label,
          contactNumber: driver.mobile || "",
        },
      }).unwrap();

      const updatedDevice = response?.device || response?.data || response;
      const nextDevice = {
        ...device,
        ...updatedDevice,
        driver: {
          ...(device?.driver || {}),
          ...(updatedDevice?.driver || {}),
          driverId:
            updatedDevice?.driver?.driverId ||
            updatedDevice?.driverId ||
            driver.id,
          driverName:
            updatedDevice?.driver?.driverName ||
            updatedDevice?.driverName ||
            driver.label,
          contactNumber:
            updatedDevice?.driver?.contactNumber ||
            updatedDevice?.driverContact ||
            driver.mobile ||
            "",
        },
      };

      setAssignedDriver({
        id: String(
          nextDevice?.driverId ||
          nextDevice?.driver?.driverId ||
          driver.id
        ),
        name:
          nextDevice?.driver?.driverName ||
          nextDevice?.driverName ||
          driver.label,
        mobile:
          nextDevice?.driver?.contactNumber ||
          nextDevice?.driverContact ||
          driver.mobile ||
          "",
      });
      if (typeof onDeviceUpdated === "function") {
        onDeviceUpdated(nextDevice);
      }
      setAssignDriverOpen(false);
      setDriverSearch("");
      showStatus("success", "Driver assigned successfully");
    } catch (error) {
      showStatus("error", error?.data?.msg || error?.data?.error || "Failed to assign driver");
    }
  }, [device, normalizedDriverAssignmentDevices, onDeviceUpdated, showStatus, updateDevice]);

  const vehicleIconSrc = useMemo(() => {
    if (!hasDevice) return null;

    const rawIconKey = String(
      device?.vehicleType ??
      device?.deviceIcon ??
      device?.vehicleIcon ??
      device?.icon ??
      'car'
    )
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '');

    return RIGHT_PANEL_VEHICLE_ICONS[rawIconKey] || rightsideCar;
  }, [device, hasDevice]);
  const routesList = useMemo(() => {
    if (Array.isArray(routesPayload)) return routesPayload;
    if (Array.isArray(routesPayload?.data)) return routesPayload.data;
    return [];
  }, [routesPayload]);
  const assignedRoute = useMemo(() => {
    const routeRef = device?.advancedSettings?.defaultRouteId;
    if (routeRef && typeof routeRef === 'object' && routeRef.routeName) {
      return routeRef;
    }
    const routeId = String(routeRef?._id || routeRef || '');
    if (!routeId) return null;
    return routesList.find((row) => String(row?._id || row?.id || '') === routeId) || null;
  }, [device?.advancedSettings?.defaultRouteId, routesList]);
  const currentUserId = String(currentUser?._id || currentUser?.id || '');
  const currentUserRole = String(currentUser?.role || '').trim().toLowerCase();
  const isViewerRole = currentUserRole === 'viewer';
  const assignedPrimaryUserId = String(device?.assignedToUser?._id || device?.assignedToUser || '');
  const assignedDriverUserId = String(device?.driver?.driverId || device?.driverId || '');
  const assignedViewerIds = Array.isArray(device?.assignedToViewers)
    ? device.assignedToViewers.map((viewer) => String(viewer?._id || viewer || ''))
    : [];
  const isRouteOperator = Boolean(
    currentUserId &&
    (
      currentUserId === assignedPrimaryUserId ||
      currentUserId === assignedDriverUserId ||
      assignedViewerIds.includes(currentUserId)
    )
  );
  const routeExecution = device?.advancedSettings?.routeExecution || {};
  const routeStatus = String(routeExecution?.status || (assignedRoute ? 'assigned' : 'unassigned')).toLowerCase();
  const canStartRoute = Boolean(assignedRoute) && isRouteOperator && routeStatus === 'assigned';
  const canCompleteRoute = Boolean(assignedRoute) && isRouteOperator && routeStatus === 'started';
  const routeStatusLabel =
    routeStatus === 'started'
      ? 'Started'
      : routeStatus === 'completed'
        ? 'Completed'
        : routeStatus === 'assigned'
          ? 'Assigned'
          : 'Not Assigned';
  const routeStatusColor =
    routeStatus === 'started'
      ? '#16a34a'
      : routeStatus === 'completed'
        ? '#2563eb'
        : routeStatus === 'assigned'
          ? '#d97706'
          : '#6b7280';

  const handleRouteStateUpdate = useCallback(async (action) => {
    if (!device?._id || !assignedRoute?._id) return;
    try {
      setIsUpdatingRouteState(true);
      const response = await api.put(`/users/my-devices/${device._id}/route-state`, { action });
      const updatedDevice = response?.data?.device;
      if (updatedDevice && typeof onDeviceUpdated === 'function') {
        onDeviceUpdated(updatedDevice);
      }
      showStatus('success', response?.data?.msg || (action === 'start' ? 'Route started.' : 'Route completed.'));
    } catch (error) {
      showStatus('error', error?.response?.data?.msg || 'Failed to update route status.');
    } finally {
      setIsUpdatingRouteState(false);
    }
  }, [assignedRoute?._id, device?._id, onDeviceUpdated, showStatus]);
  const mapTypes = ['roadmap', 'satellite', 'terrain', 'hybrid'];
  const cycleMapType = () => {
    if (!setMapType) return;
    const currentIndex = mapTypes.indexOf(mapType);
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % mapTypes.length;
    setMapType(mapTypes[nextIndex]);
  };
  const actionRailButtons = [
    {
      key: 'fullscreen',
      label: fullscreen ? 'Exit Fullscreen' : 'Fullscreen Map',
      icon: fullscreen ? <FullscreenExitIcon sx={{ fontSize: 19 }} /> : <FullscreenIcon sx={{ fontSize: 19 }} />,
      active: fullscreen,
      onClick: () => onToggleFullscreen?.(),
    },
    {
      key: 'locate',
      label: 'My Location',
      icon: <MyLocationIcon sx={{ fontSize: 19 }} />,
      onClick: () => onLocateMe?.(),
    },
    {
      key: 'follow',
      label: followMode ? 'Following' : 'Follow',
      icon: <NavigationIcon sx={{ fontSize: 19, transform: 'rotate(18deg)' }} />,
      active: followMode,
      disabled: !hasSelectedDevice,
      onClick: () => hasSelectedDevice && setFollowMode?.(!followMode),
    },
    {
      key: 'fit',
      label: 'View All',
      icon: <FitBoundsIcon sx={{ fontSize: 19 }} />,
      onClick: () => onFitBounds?.(),
    },
    {
      key: 'places',
      label: placesViewActive ? 'Hide Places View' : 'Show Places',
      icon: <PlacesIcon sx={{ fontSize: 19 }} />,
      active: placesViewActive,
      onClick: () => onTogglePlacesView?.(),
    },
    {
      key: 'map-type',
      label: `Map: ${mapType}`,
      icon: <MapTypeIcon sx={{ fontSize: 19 }} />,
      onClick: cycleMapType,
    },
    {
      key: 'traffic-toggle',
      label: 'Traffic',
      icon: <TrafficIcon sx={{ fontSize: 19 }} />,
      active: showTraffic,
      onClick: () => setShowTraffic?.(!showTraffic),
    },
    {
      key: 'collapse',
      label: isRailOnly ? 'Expand' : 'Collapse',
      icon: (
        <CollapseIcon
          sx={{
            fontSize: 19,
            transform: isRailOnly ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s ease',
          }}
        />
      ),
      onClick: () => setCollapsed((prev) => !prev),
      disabled: !hasDevice,
    },
    {
      key: 'camera',
      label: 'Camera',
      icon: <VideoIcon sx={{ fontSize: 19 }} />,
      disabled: !hasDevice,
      onClick: () => onOpenCamera?.(),
      hidden: isViewerRole,
    },
    {
      key: 'navigate',
      label: 'Navigate',
      icon: <NavigationIcon sx={{ fontSize: 19, transform: 'rotate(18deg)' }} />,
      disabled: !hasDevice,
      onClick: () => {
        if (computedData.lat && computedData.lng) {
          getDirections?.(computedData.lat, computedData.lng);
        }
      },
    },
    {
      key: 'command',
      label: 'Command',
      icon: (
        <Box
          component="img"
          src={engineIcon}
          alt="Engine command"
          sx={{
            width: 19,
            height: 19,
            display: 'block',
            objectFit: 'contain',
            filter: 'brightness(0) invert(1)',
          }}
        />
      ),
      disabled: !hasDevice,
      onClick: () => handleCommand?.(),
      hidden: isViewerRole,
    },
  ].filter((action) => !action.hidden);
  if (!isOpen) return null;

  return (
    <Box
      ref={panelRef}
      sx={{
        position: 'fixed',
        // top: isMobile ? 118 : 'calc(var(--navbar-offset, 64px) - 6px)',
        top: '8px',
        right: isMobile
          ? 10
          : isRailOnly
            ? 8
            : 'calc(var(--map-shell-padding, 14px) + 18px)',
        left: 'auto',
        bottom: isMobile ? 10 : `calc(10px + ${bottomOffset}px)`,
        zIndex: 1300,
        display: 'flex',
        flexDirection: isMobile ? 'column-reverse' : 'row',
        alignItems: isMobile ? 'stretch' : 'flex-start',
        gap: isMobile ? 0.75 : 4,
        width: isMobile
          ? panelWidth
          : isRailOnly
            ? 'var(--right-rail-width, 3rem)'
            : `calc(${panelWidth} + var(--right-rail-width, 3rem))`,
        maxWidth: isMobile
          ? panelWidth
          : isRailOnly
            ? 'var(--right-rail-width, 3rem)'
            : `calc(${panelWidth} + var(--right-rail-width, 3rem))`,
        maxHeight: isMobile
          ? 'calc(100dvh - 128px)'
          : `calc(100dvh - var(--navbar-offset, 64px) + 4px - ${bottomOffset}px)`,
        pointerEvents: 'auto',
        justifyContent: isRailOnly && !isMobile ? 'flex-end' : 'flex-start',
      }}
    >
      <Stack
        direction={isMobile ? 'row' : 'column'}
        spacing={isMobile ? 0.75 : (isCompactDesktop ? 0.45 : 1)}
        sx={{
          alignItems: 'center',
          pt: 4,
          overflowX: isMobile ? 'auto' : 'visible',
          overflowY: isMobile ? 'hidden' : 'auto',
          maxHeight: isMobile ? 'none' : '100%',
          flexShrink: 0,
          pb: 0,
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {actionRailButtons.map((action) => (
          <Box key={action.key} sx={{ position: 'relative' }}>
            <Tooltip title={action.label} placement="left" arrow>
              <span>
                <IconButton
                  onClick={action.onClick}
                  disabled={action.disabled}
                  sx={{
                    width: isMobile ? 38 : (isCompactDesktop ? 30 : 40),
                    height: isMobile ? 38 : (isCompactDesktop ? 30 : 40),
                    minWidth: 0,
                    borderRadius: 0,
                    bgcolor: action.active ? 'var(--logo-blue, #1d63c8)' : '#1f2937',
                    color: '#fff',
                    opacity: action.disabled ? 0.45 : 1,
                    boxShadow: action.active
                      ? '0 8px 18px rgba(29,99,200,0.26)'
                      : '0 8px 18px rgba(15,23,42,0.16)',
                    '&:hover': {
                      bgcolor: action.active ? 'var(--logo-blue-hover, #114fae)' : '#111827',
                    },
                  }}
                >
                  {React.isValidElement(action.icon)
                    ? React.cloneElement(action.icon, {
                        sx: {
                          ...(action.icon.props?.sx || {}),
                          fontSize: isCompactDesktop && !isMobile ? 15 : (action.icon.props?.sx?.fontSize || 19),
                        },
                      })
                    : action.icon}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))}

        <Stack spacing={isCompactDesktop ? 0.45 : 1} sx={{ mt: isCompactDesktop ? 0.15 : 0.75 }}>
          <Tooltip title="Zoom In" placement="left" arrow>
            <span>
              <IconButton
                onClick={() => onZoomIn?.()}
                sx={{
                  width: isMobile ? 38 : (isCompactDesktop ? 30 : 40),
                  height: isMobile ? 38 : (isCompactDesktop ? 30 : 40),
                  minWidth: 0,
                  borderRadius: 0,
                  bgcolor: '#1f2937',
                  color: '#fff',
                }}
              >
                <PlusIcon sx={{ fontSize: isCompactDesktop && !isMobile ? 15 : 20 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zoom Out" placement="left" arrow>
            <span>
              <IconButton
                onClick={() => onZoomOut?.()}
                sx={{
                  width: isMobile ? 38 : (isCompactDesktop ? 30 : 40),
                  height: isMobile ? 38 : (isCompactDesktop ? 30 : 40),
                  minWidth: 0,
                  borderRadius: 0,
                  bgcolor: '#1f2937',
                  color: '#fff',
                }}
              >
                <MinusIcon sx={{ fontSize: isCompactDesktop && !isMobile ? 15 : 20 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Paper
        elevation={10}
        sx={{
          width: isMobile ? panelWidth : panelWidth,
          maxWidth: isMobile ? panelWidth : panelWidth,
          minWidth: isMobile ? 0 : (isCompactDesktop ? 'min(100%, 228px)' : 'min(100%, 240px)'),
          height: isMobile ? 'min(calc(100dvh - 184px), 72vh)' : '100%',
          maxHeight: '100%',
          bgcolor: 'transparent',
          borderRadius: 0,
          overflow: 'hidden',
          boxShadow: 'none',
          border: 'none',
          display: isRailOnly ? 'none' : 'flex',
          flexDirection: 'column',
          position: 'relative',
          '& .MuiTypography-h6': {
            fontSize: `${1.16 * panelTextScale}rem`,
          },
          '& .MuiTypography-body2': {
            fontSize: `${0.9 * panelTextScale}rem`,
          },
          '& .MuiTypography-caption': {
            fontSize: `${0.76 * panelTextScale}rem`,
          },
          '& .MuiChip-label': {
            fontSize: `${0.8 * panelTextScale}rem`,
            px: 1.1,
          },
          '& .MuiButton-root': {
            fontSize: `${0.82 * panelTextScale}rem`,
          },
        }}
      >
        <Tooltip title="Close" placement="left" arrow>
          <span>
            <IconButton
              size="small"
              onClick={onClose}
              disabled={!hasDevice}
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                zIndex: 2,
                color: '#6b7280',
                bgcolor: 'rgba(255,255,255,0.96)',
                borderRadius: 0,
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: 0.45,
            pt: 4,
            pb: 2,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'linear-gradient(180deg, #f8fbff 0%, var(--logo-blue-tint, #e9f6ff) 100%)',
              borderRadius: '999px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'linear-gradient(180deg, var(--logo-blue-soft, #78cfff) 0%, var(--logo-blue, #1d63c8) 100%)',
              borderRadius: '999px',
              border: '1px solid rgba(255, 255, 255, 0.65)',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'linear-gradient(180deg, var(--logo-blue, #1d63c8) 0%, var(--logo-blue-hover, #114fae) 100%)',
            },
          }}
        >
        {!hasDevice ? null : (
          <>
        {/* Vehicle Image & Status */}
        <Box sx={{ p: 0.55, bgcolor: 'transparent' }}>
          <Box
            sx={{
              bgcolor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: 0,
              p: isMobile ? 1.2 : 1.35,
              textAlign: 'center',
              mb: 1.15,
              boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.9rem',
                fontWeight: 900,
                color: '#1f2937',
                textAlign: 'left',
                mb: 1,
                lineHeight: 1.15,
              }}
            >
              {device?.vehicleNumber || device?.name || 'Vehicle Details'}
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: isMobile ? 110 : 132,
                bgcolor: 'transparent',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
                overflow: 'hidden',
              }}
            >
              {vehicleIconSrc ? (
                <Box
                  component="img"
                  src={vehicleIconSrc}
                  alt={device?.name || 'Vehicle'}
                  sx={{
                    width: '86%',
                    height: '86%',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 10px 12px rgba(15,23,42,0.18))',
                  }}
                />
              ) : (
                <Typography variant="caption" color="textSecondary">
                  {device.vehicleNumber || 'Vehicle Image'}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.1,
                minWidth: 0,
                background: '#f8fafc',
                borderRadius: 0,
                px: 1,
                py: 0.9,
              }}
            >
              <Box
                sx={{
                  bgcolor: statusIconTheme.badge,
                  color: '#fff',
                  fontWeight: 900,
                  fontSize: isMobile ? '0.72rem' : '0.78rem',
                  borderRadius: 0,
                  px: 1.2,
                  py: 0.7,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {isNotReporting
                  ? 'NOT R...'
                  : formatStatusLabel(computedData.vehicleStatus)}
              </Box>
              <Typography
                variant="body2"
                fontWeight={800}
                sx={{ fontSize: isMobile ? '0.8rem' : '0.86rem', color: '#1f2937', letterSpacing: '0.02em' }}
              >
                {getElapsedTime()}
              </Typography>
            </Box>
            <Box
              sx={{
                mt: 1.05,
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0,
                borderRadius: 0,
                overflow: 'hidden',
                border: '1px solid #e5e7eb',
              }}
            >
              {String(Math.max(Math.round(computedData.totalOdometerKm || 0), 0))
                .padStart(7, '0')
                .split('')
                .map((digit, index) => (
                  <Box
                    key={`${digit}-${index}`}
                    sx={{
                      py: 0.68,
                      bgcolor: '#ffffff',
                      borderRight: index < 6 ? '1px solid #e5e7eb' : 'none',
                      fontWeight: 500,
                      fontSize: isMobile ? '0.88rem' : '0.94rem',
                      color: '#1f2937',
                    }}
                  >
                    {digit}
                  </Box>
                ))}
            </Box>

            <Box sx={{ mt: 1.35, display: 'grid', gap: 1.05 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography sx={infoRowLabelSx}>Driver</Typography>
                <Button
                  size="small"
                  onClick={() => setAssignDriverOpen(true)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    color: '#2563eb',
                    p: 0,
                    minWidth: 'auto',
                    fontWeight: 700,
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  Assign Driver
                </Button>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography sx={infoRowLabelSx}>Mobile</Typography>
                <Typography sx={infoRowValueSx}>{assignedDriver.mobile || 'NA'}</Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 1.15, borderColor: '#e5e7eb' }} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                alignItems: 'center',
                justifyItems: 'center',
                gap: 0,
              }}
            >
              {[
                {
                  key: 'satellite',
                  icon: <SatelliteIcon sx={{ fontSize: 22, color: statusIconTheme.gps }} />,
                  title: `GPS: ${computedData.satellites ?? 'NA'}`,
                },
                {
                  key: 'signal',
                  icon: <KeyIcon sx={{ fontSize: 20, color: statusIconTheme.ignition }} />,
                  title: computedData.accStatus ? 'Ignition On' : 'Ignition Off',
                },
                {
                  key: 'battery',
                  icon: <BatteryIcon sx={{ fontSize: 20, color: statusIconTheme.battery }} />,
                  title: `Battery: ${computedData.battery != null ? `${computedData.battery}%` : 'NA'}`,
                },
                {
                  key: 'power',
                  icon: <PowerOffIcon sx={{ fontSize: 20, color: statusIconTheme.power }} />,
                  title: computedData.accStatus ? 'Power On' : 'Power Off',
                },
                {
                  key: 'immobilized',
                  icon: (
                    <Box
                      component="img"
                      src={engineIcon}
                      alt="Immobilization"
                      sx={{
                        width: 21,
                        height: 21,
                        display: 'block',
                        objectFit: 'contain',
                        filter: statusIconTheme.immobilization === '#e63946'
                          ? 'brightness(0) saturate(100%) invert(24%) sepia(97%) saturate(2677%) hue-rotate(339deg) brightness(98%) contrast(93%)'
                          : 'none',
                      }}
                    />
                  ),
                  title: 'Immobilization',
                },
              ].map((item) => (
                <Tooltip key={item.key} title={item.title} placement="top" arrow>
                  <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 28 }}>
                    {item.icon}
                  </Box>
                </Tooltip>
              ))}
            </Box>

            <Box
              sx={{
                mt: 1.2,
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 0.8,
              }}
            >
              {[
                {
                  key: 'battery-value',
                  label: 'Battery',
                  value: computedData.battery != null ? `${computedData.battery}%` : 'NA',
                },
                {
                  key: 'voltage-value',
                  label: 'Voltage',
                  value: computedData.voltage != null ? `${computedData.voltage} V` : 'NA',
                },
                {
                  key: 'temp-value',
                  label: 'Temp',
                  value: computedData.temperature != null ? `${computedData.temperature}°C` : 'NA',
                },
              ].map((item) => (
                <Box
                  key={item.key}
                  sx={{
                    border: '1px solid #e5e7eb',
                    bgcolor: '#fff',
                    px: 1,
                    py: 0.85,
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#6b7280', textTransform: 'uppercase' }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ mt: 0.25, fontSize: '0.82rem', fontWeight: 800, color: '#111827' }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Location */}
        <Box
          sx={sectionCardSx}
        >
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="textSecondary" sx={sectionTitleSx}>
              Location
              <InfoIcon sx={{ fontSize: 15, ml: 0.5, verticalAlign: 'text-bottom' }} />
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: 'transparent',
              p: 0,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 1, lineHeight: 1.4 }}>
              {isLoadingAddress
                ? 'Resolving address...'
                : resolvedAddress || normalizeAddressText(computedData.address) || 'Resolving address...'}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#2563eb', fontWeight: 500, letterSpacing: '0.01em' }}>
              {computedData.lat?.toFixed(6)}° , {computedData.lng?.toFixed(6)}°
            </Typography>
          </Box>
        </Box>

        <Box
          sx={sectionCardSx}
        >
          <Typography variant="caption" fontWeight={800} sx={sectionTitleSx}>
            Time Sync
          </Typography>
          <Stack spacing={0.85}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" sx={{ ...infoRowLabelSx, fontWeight: 700 }}>
                Device Time
              </Typography>
              <Typography variant="caption" sx={{ ...infoRowValueSx, fontWeight: 700 }}>
               {isNotReporting || isNoDataDevice ? "N/A" : formatDisplayTime(computedData.deviceTime)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" sx={{ ...infoRowLabelSx, fontWeight: 700 }}>
                Server Time
              </Typography>
              <Typography variant="caption" sx={{ ...infoRowValueSx, fontWeight: 700 }}>
                {isNotReporting || isNoDataDevice ? "N/A" : formatDisplayTime(computedData.serverTime)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" sx={{ ...infoRowLabelSx, fontWeight: 700 }}>
                Corrected Time
              </Typography>
              <Typography variant="caption" sx={{ ...infoRowValueSx, fontWeight: 700 }}>
                {isNotReporting || isNoDataDevice ? "N/A" : formatDisplayTime(computedData.correctedTime)}
              </Typography>
            </Box>
{isNoDataDevice ? (
  <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 800 }}>
    No Data
  </Typography>
) : isNotReporting ? (
  <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#0ea5e9', fontWeight: 800 }}>
    Not Reporting
  </Typography>
) : computedData.isDrifted ? (
              <>
                <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#dc2626', fontWeight: 800 }}>
                  Warning Time Drift Detected ({formatDriftText(computedData.driftMs)})
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#dc2626', fontWeight: 700 }}>
                  Using Server Time
                </Typography>
              </>
            ) : (
              <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#16a34a', fontWeight: 800 }}>
                Time Synced
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Today Activity */}
        <Box
          sx={sectionCardSx}
        >
          <Typography variant="caption" fontWeight={800} sx={sectionTitleSx}>
            Today Activity
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.1,
              px: 0.25,
            }}
          >
            <Box sx={{ color: '#7cc243', fontSize: '1.5rem', lineHeight: 1 }}>⚑</Box>
            <Box
              sx={{
                flex: 1,
                mx: 1,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: '0.82rem', color: '#1f2937', fontWeight: 700, lineHeight: 1 }}>
                {dailyStats.distance.toFixed(2)} km
              </Typography>
              <Box
                sx={{
                  mt: 0.35,
                  height: '2px',
                  width: '100%',
                  borderTop: '2px dashed #111827',
                }}
              />
            </Box>
            <Box sx={{ color: '#1d9bf0', fontSize: '1.45rem', lineHeight: 1 }}>🚗</Box>
          </Box>

          <Stack spacing={0.7}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.82rem', color: '#111827', fontWeight: 500 }}>Running</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#16a34a', fontWeight: 700 }}>
                {activityTimeLabel(dailyStats.runningTime)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.82rem', color: '#111827', fontWeight: 500 }}>Idle</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#d99a12', fontWeight: 700 }}>
                {activityTimeLabel(dailyStats.idleTime)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.82rem', color: '#111827', fontWeight: 500 }}>Stop</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#ef4444', fontWeight: 700 }}>
                {activityTimeLabel(dailyStats.stopTime)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.82rem', color: '#111827', fontWeight: 500 }}>Inactive</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#3749ff', fontWeight: 700 }}>
                {activityTimeLabel(dailyStats.inactiveTime)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.76rem', color: '#111827', fontWeight: 500 }}>Fuel Consumption</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#111827', fontWeight: 500 }}>
                {dailyStats.fuelConsumption === null ? 'N/A' : `${dailyStats.fuelConsumption.toFixed(2)} Ltr`}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Speed Stats */}
        <Box sx={{ px: 1, mb: 1.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            {/* Avg Speed */}
            <Box
              sx={{
                bgcolor: '#33a8ff',
                borderRadius: 0,
                p: 1.35,
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(51,168,255,0.18)',
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.82rem', color: '#062f4f', display: 'block', mb: 0.5, fontWeight: 800 }}>
                Avg Speed
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.2rem', color: '#062f4f' }}>
                {isNoDataDevice ? "N/A" : `${dailyStats.avgSpeed.toFixed(2)} kmh`}
              </Typography>
            </Box>

            {/* Max Speed */}
            <Box
              sx={{
                bgcolor: '#ff6570',
                borderRadius: 0,
                p: 1.35,
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(255,101,112,0.18)',
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.82rem', color: '#4b1117', display: 'block', mb: 0.5, fontWeight: 800 }}>
                Max Speed
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.2rem', color: '#4b1117' }}>
                {isNoDataDevice ? "N/A" : `${dailyStats.maxSpeed.toFixed(0)} kmh`}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Network Parameters */}
        <Box
          sx={sectionCardSx}
        >
          <Typography variant="caption" fontWeight={800} sx={sectionTitleSx}>
            Network Parameter
          </Typography>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                Radio Type
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                {networkParams.radioType || 'NA'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                Cell Id
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                {networkParams.cellId ?? 'NA'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                Area Code
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                {networkParams.lac ?? 'NA'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                Country Code
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                {networkParams.mcc ?? 'NA'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                Network Code
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                {networkParams.mnc ?? 'NA'}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box sx={sectionCardSx}>
          <Stack spacing={1.1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={sectionTitleSx}>
                Assigned Route
              </Typography>
              <Chip
                size="small"
                label={routeStatusLabel}
                sx={{
                  height: 24,
                  fontWeight: 800,
                  bgcolor: `${routeStatusColor}15`,
                  color: routeStatusColor,
                  border: `1px solid ${routeStatusColor}30`,
                  mb: 0.8,
                }}
              />
            </Box>

            {assignedRoute ? (
              <>
                <Typography variant="body2" sx={{ fontSize: '0.84rem', fontWeight: 800, color: '#111827' }}>
                  {assignedRoute?.routeName || 'Assigned Route'}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.77rem', color: '#6b7280', lineHeight: 1.45 }}>
                  From: {assignedRoute?.source?.address || 'NA'}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.77rem', color: '#6b7280', lineHeight: 1.45 }}>
                  To: {assignedRoute?.destination?.address || 'NA'}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.77rem', color: '#6b7280' }}>
                  Stops: {Array.isArray(assignedRoute?.stops) ? assignedRoute.stops.length : 0}
                </Typography>
                {isRouteOperator ? (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!canStartRoute || isUpdatingRouteState}
                      onClick={() => handleRouteStateUpdate('start')}
                      sx={{ flex: 1, textTransform: 'none', fontWeight: 800, bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
                    >
                      {isUpdatingRouteState && canStartRoute ? 'Starting...' : 'Start Route'}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!canCompleteRoute || isUpdatingRouteState}
                      onClick={() => handleRouteStateUpdate('complete')}
                      sx={{ flex: 1, textTransform: 'none', fontWeight: 800, bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}
                    >
                      {isUpdatingRouteState && canCompleteRoute ? 'Ending...' : 'End Route'}
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="caption" sx={{ fontSize: '0.76rem', color: '#6b7280', fontWeight: 600 }}>
                    Route actions sirf assigned driver/user ko dikhenge.
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
                No route assigned yet.
              </Typography>
            )}
          </Stack>
        </Box>
        </>
        )}
        </Box>
      </Paper>

      <Dialog
        open={assignDriverOpen}
        onClose={() => {
          setAssignDriverOpen(false);
          setDriverSearch("");
        }}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "min(640px, calc(100vw - 80px))",
            borderRadius: 2.5,
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ p: 2.5, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#111827" }}>
            Assign Driver
          </Typography>
          <IconButton
            onClick={() => {
              setAssignDriverOpen(false);
              setDriverSearch("");
            }}
            size="small"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <Box sx={{ p: 2.5 }}>
          <TextField
            fullWidth
            placeholder="Search"
            value={driverSearch}
            onChange={(event) => setDriverSearch(event.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: "#fb7185" }} />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              mt: 2,
              maxHeight: 320,
              overflow: "auto",
              borderRadius: 2,
            }}
          >
            {availableDrivers.map((driver) => {
              const checked = String(assignedDriver.id || "") === String(driver.id);
              return (
                <Box
                  key={driver.id}
                  onClick={() => !isAssigningDriver && handleAssignDriver(driver)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 1,
                    px: 0.5,
                    cursor: isAssigningDriver ? "wait" : "pointer",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <Checkbox
                    checked={checked}
                    disabled={isAssigningDriver}
                    sx={{ p: 0.5 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.98rem", color: "#111827" }}>
                      {driver.label}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: "#6b7280" }}>
                      {driver.mobile || "NA"}
                    </Typography>
                  </Box>
                </Box>
              );
            })}

            {!availableDrivers.length && (
              <Typography sx={{ py: 2, color: "#6b7280", fontSize: "0.9rem" }}>
                No drivers found
              </Typography>
            )}
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default RightSidebarPanel;
